/**
 *
 * useMixpanelTracking
 *
 */

import { useRef, useEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { makeSelectAuth } from 'containers/Auth/selectors';
import qs from 'qs';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _has from 'lodash/has';
import Mixpanel from 'utils/mixpanelService';
import UserInteraction from 'utils/userInteractionService';

const TOP_LEVEL_PAGES = ['resources', 'lists'];

function useMixpanelTracking({ eventTitle, type, item }) {
  const hasTracked = useRef(false);
  const auth = useSelector(makeSelectAuth());
  const history = useHistory();
  const location = useLocation();
  const { slug } = useParams();
  const fromMapping = {
    series: /series\/.+$/,
    coursePage: /courses\/.+$/,
  };
  const fromPath = document.referrer
    ? Object.keys(fromMapping).find(key => {
        const regex = fromMapping[key];
        return regex.test(document.referrer);
      })
    : undefined;

  useEffect(() => {
    const params = qs.parse(location.search.slice(1));
    const track = async () => {
      if (auth.isLoaded && !_isEmpty(item) && !hasTracked.current) {
        const {
          fields: {
            name,
            title,
            article,
            type: resourceSubtype,
            blog: isBlog,
            topics = [],
            client,
          },
          sys: { id },
        } = item;

        const searchTerm = params['search-term'];
        let subtype;
        let blog;
        const clientExclusive = !_isEmpty(client);
        if (type === 'app') {
          if (resourceSubtype === 'Online Therapy') subtype = resourceSubtype;
          else subtype = 'Apps';
        }
        if (type === 'organization') {
          subtype = resourceSubtype;
        }
        if (type === 'read') {
          subtype = resourceSubtype;
          blog = isBlog || false;
        }
        const approach = _get(topics, '0.fields.approach');
        const topicsList = topics
          .map(topic => _get(topic, 'fields.title', null))
          .filter(topic => topic !== null)
          .map(topicName => `[${topicName}]`)
          .join();

        const trackProperties = {
          name: name || title || article,
          id,
          type: type === 'read' ? 'article' : type,
          subtype,
          blog,
          searchTerm,
          topic_name: topicsList,
          clientExclusive,
          approach,
          path: fromPath,
        };

        Mixpanel.track(eventTitle, trackProperties);

        hasTracked.current = true;

        if (searchTerm) {
          delete params['search-term'];
          history.replace(`${location.pathname}?${qs.stringify(params)}`);
        }
        if (!_isEmpty(topics)) {
          UserInteraction.registerTopicInteraction({
            type: 'topic-related-resource-view',
            topic: _get(topics, '0.fields.title', null),
            resource: id,
          });
        }
        let finalType = type;
        if (isBlog) finalType = 'blog';
        else if (subtype === 'News') finalType = 'news';
        else if (type === 'read') finalType = 'article';

        if (type !== 'topic') {
          UserInteraction.registerGeneralInteraction({
            type: 'resource-page-view',
            resourceID: id,
            resourceAudiences: _get(item, 'fields.audienceType', [])
              .filter(
                entry =>
                  _has(entry, 'fields.name') &&
                  _get(entry, 'fields.reviewStatus') === 'Accepted',
              )
              .map(entry => `[${_get(entry, 'fields.name')}]`)
              .join(),
            resourceTags: _get(item, 'fields.tags', [])
              .filter(
                tag =>
                  _has(tag, 'fields.title') &&
                  _get(tag, 'fields.reviewStatus') === 'Accepted',
              )
              .map(tag => `[${_get(tag, 'fields.title')}]`)
              .join(),
            resourceType: finalType,
          });
        }
      } else if (
        auth.isLoaded &&
        TOP_LEVEL_PAGES.includes(type) &&
        !hasTracked.current
      ) {
        hasTracked.current = true;
        const searchTerm = params['search-term'];

        if (searchTerm) {
          const trackProperties = {
            searchTerm,
          };
          Mixpanel.track(eventTitle, trackProperties);

          delete params['search-term'];
          history.replace(`${location.pathname}?${qs.stringify(params)}`);
        } else {
          Mixpanel.track(eventTitle);
        }
      }
    };
    track();
  }, [auth, item]);

  useEffect(() => {
    if (slug) hasTracked.current = false;
  }, [slug]);

  return null;
}

export default useMixpanelTracking;
